import React from 'react';
import { AspectRatio } from 'react-aspect-ratio';
import { Link } from 'react-router-dom';

import { Box, Stack, Typography } from '@mui/material';

import styled from '@emotion/styled';
import dayjs from 'dayjs';

import { OfferCategory, VennueAccessRequestItem, VenueAccessREnum } from '../../../graphql/API';
import LinkProfile from '../../../pages/Collabs/components/LinkProfile';
import Icons from '../../../themes/icons';
import { formatNumber } from '../../../utils/formatter';
import LazyAvatar from '../../Avatar/LazyAvatar/LazyAvatar';
import LoadingButton from '../../Button/LoadingButton';
import { InstagramGradientIcon } from '../../Icons/InstagramGradientIcon';
import InstagramTag from '../../InstagramTag/InstagramTag';
import { OnAction } from '../../Tables/AccessRequest/useAction';
import { buttonLoadingState, isOldRequest, mapToActionRequest } from '../../Tables/AccessRequest/utils';
import TikTokTag from '../../TikTokTag/TikTokTag';

type Props = {
  accessRequest: VennueAccessRequestItem;
  fullProfile: boolean;
  loadingState: Record<string, boolean>;
  onAction: OnAction;
  onAccept?: (accessRequest: VennueAccessRequestItem | null) => void;
  onReject?: (accessRequest: VennueAccessRequestItem | null) => void;
  onRejectWithConfirmation?: (accessRequest: VennueAccessRequestItem | null) => void;
  isOnlineBrand?: boolean;
  isPromotionLimitHit?: boolean;
  onAcceptIncentive?: (accessRequest: VennueAccessRequestItem | null) => void;
  onAcceptOldRequest?: (accessRequest: VennueAccessRequestItem | null) => void;
};

export const AccessRequestUserProfileCard: React.FC<Props> = ({
  accessRequest,
  fullProfile,
  loadingState,
  onAction,
  onAccept,
  onReject,
  onRejectWithConfirmation,
  isOnlineBrand = false,
  isPromotionLimitHit = false,
  onAcceptIncentive,
  onAcceptOldRequest,
}) => {
  return (
    <Box
      display='flex'
      flexDirection='column'
      sx={{
        border: '1px solid #e1e4eb',
        borderRadius: '12px',
        padding: 0,
        overflow: 'hidden',
        backgroundColor: '#fff',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.03)',
        height: '100%',
        width: '100%',
      }}
    >
      <Box
        component={Link}
        to={`/users/${accessRequest.userID}?offerID=${accessRequest?.offerID}`}
        position={'relative'}
        sx={{ display: 'block' }}
      >
        <AspectRatio>
          <LazyAvatar
            variant='rounded'
            src={String(accessRequest?.user?.picture?.medium)}
            placeholderSrc={String(accessRequest?.user?.picture?.thumbnail)}
          />
        </AspectRatio>
        <RatingAndIncentiveContainer>
          {!!accessRequest?.user?.rating && (
            <RatingContainer rating={accessRequest.user.rating}>
              <Icons.StarIcon />
              <span>{accessRequest.user.rating.toFixed(1)}</span>
            </RatingContainer>
          )}
          {(accessRequest?.incentiveValue || 0) > 0 && (
            <IncentiveContainer>
              <Icons.DollarSignV3Icon />
            </IncentiveContainer>
          )}
        </RatingAndIncentiveContainer>
        {(accessRequest?.totalRequested || 0) > 1 && (
          <RequestedContainer>{accessRequest?.totalRequested} Requests</RequestedContainer>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          padding: '12px',
        }}
      >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <LinkProfile
          fontSize='16px'
          fontWeight='600'
          url={`/users/${accessRequest.userID}?offerID=${accessRequest?.offerID}`}
          label={`${accessRequest?.user?.firstname} ${accessRequest?.user?.lastname}`}
          hoverTextDecoration='underline'
        />
        {!isPromotionLimitHit && (
          <Stack
            direction='row'
            spacing={1}
            flexWrap='nowrap'
            useFlexGap
            sx={{ mt: 0.5, mb: 0.5, width: '100%', minWidth: 0 }}
          >
            {accessRequest?.user?.instagramHandle && (
              <SocialChip>
                <InstagramGradientIcon style={{ width: 14, height: 14, flexShrink: 0 }} />
                <InstagramTag
                  username={accessRequest?.user?.instagramHandle}
                  textColor='#1E55A1'
                  showAtSign={false}
                  customFontSize='13px'
                  sx={ellipsisTagSx}
                />
              </SocialChip>
            )}
            {accessRequest?.user?.tiktokHandle && (
              <SocialChip>
                <Icons.TiktokIcon style={{ width: 14, height: 14, flexShrink: 0 }} />
                <TikTokTag
                  tiktokHandle={accessRequest?.user?.tiktokHandle}
                  textColor='#1E55A1'
                  showAtSign={false}
                  customFontSize='13px'
                  sx={ellipsisTagSx}
                />
              </SocialChip>
            )}
          </Stack>
        )}
      </Box>
      <Box
        component={Link}
        sx={{
          fontSize: '13px',
          fontWeight: 400,
          color: '#111',
          textDecoration: 'none',
          '& .offer-title': { fontWeight: 600 },
          ':hover .offer-title': { textDecoration: 'underline' },
        }}
        to={`/offers/${accessRequest.offerID}`}
      >
        {isOnlineBrand ? 'Requested' : 'Requested access to'}{' '}
        <Box component='span' className='offer-title'>
          {accessRequest.offer?.title}
        </Box>
      </Box>
      {accessRequest?.shippingAddress && accessRequest?.offer?.category === OfferCategory.product && (
        <Typography sx={{ fontSize: '13px', fontWeight: 400, color: '#111', mt: 0.5 }}>
          Ship to {accessRequest?.shippingAddress?.city},{' '}
          {accessRequest?.shippingAddress?.country}
        </Typography>
      )}
      {accessRequest?.createdAt && (
        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#9e9e9e', mt: 0.5 }}>
          Requested {dayjs(accessRequest.createdAt).fromNow()}
        </Typography>
      )}
      <Stack spacing={1} direction='row' mt='auto' pt={2.5}>
        <LoadingButton
          fullWidth
          variant='outlined'
          onClick={() => {
            if (onRejectWithConfirmation && accessRequest?.totalRequested && accessRequest?.totalRequested < 2) {
              onRejectWithConfirmation(accessRequest);
            } else {
              if (isOnlineBrand) {
                if (onReject && (accessRequest?.totalRequested || 0) >= 2) {
                  onReject(accessRequest);
                } else {
                  onAction(mapToActionRequest(accessRequest ?? undefined, VenueAccessREnum.declined));
                }
              } else {
                onAction(mapToActionRequest(accessRequest ?? undefined, VenueAccessREnum.declined));
              }
            }
            if (onAccept) onAccept(null);
          }}
          {...buttonLoadingState(loadingState, accessRequest, VenueAccessREnum.declined)}
        >
          Reject
        </LoadingButton>
        <LoadingButton
          fullWidth
          variant='contained'
          onClick={() => {
            if (isOldRequest(accessRequest?.createdAt) && onAcceptOldRequest) {
              onAcceptOldRequest(accessRequest);
              return;
            }
            if ((accessRequest?.incentiveValue || 0) > 0) {
              if (onAcceptIncentive) onAcceptIncentive(accessRequest);
            } else {
              onAction(mapToActionRequest(accessRequest ?? undefined, VenueAccessREnum.accepted));
              if (onAccept) onAccept(accessRequest);
            }
          }}
          {...buttonLoadingState(loadingState, accessRequest, VenueAccessREnum.accepted)}
        >
          Accept
        </LoadingButton>
      </Stack>
      </Box>
    </Box>
  );
};

const RatingAndIncentiveContainer = styled.div`
  position: absolute;
  top: 2%;
  left: 2%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

const RatingContainer = styled.div<{ rating: number }>`
  border-radius: 24px;
  background: rgba(0, 0, 0, 0.6);

  color: ${(props) =>
    props.rating >= 4 ? '#03BD7E' : props.rating < 4 && props.rating > 0 ? '#FF7282' : '#fff'};

  display: inline-flex;
  padding: 6px 12px;
  align-items: center;
  gap: 8px;

  font-size: 15px;
  font-weight: 500;
`;

const RequestedContainer = styled.div`
  position: absolute;
  top: 2%;
  right: 2%;
  border-radius: 24px;
  background: rgba(0, 0, 0, 0.6);

  color: #fff;

  display: inline-flex;
  padding: 6px 12px;
  align-items: center;
  gap: 8px;

  font-size: 15px;
  font-weight: 500;
`;

const IncentiveContainer = styled.div`
  display: flex;
  width: 38px;
  height: 38px;
  padding: 6px 12px;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.6);
`;

const SocialChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  background-color: #f5f5f5;
  border: 1px solid #efefef;
  min-width: 0;
  overflow: hidden;
`;

const ellipsisTagSx = {
  fontWeight: 500,
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;
